import React, { useState, useEffect, useCallback, useRef } from "react";
import { btn } from "../../../shared/styles/buttonStyles";
import { selectedSliderMarkSx, standardSliderSx } from "../../../shared/styles/sliderStyles";
import axios from "axios";
import { getApiErrorMessage } from "../../../shared/utils/apiError";
import { useMsal } from "@azure/msal-react";
import { useLoading } from "../../../shared/state/LoadingContext";
import { toast } from "react-toastify";
import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";
import FeatureTabs, { FeatureTab } from "../../../shared/ui/FeatureTabs";
import FeatureInformationPanel from "../../../shared/ui/FeatureInformationPanel";
import ResourceContextPanel from "../../../shared/ui/ResourceContextPanel";
import ActivityLogTable, { ActivityLogTableItem } from "../../../shared/ui/ActivityLogTable";
import ConfirmActionDialog from "../../../shared/ui/ConfirmActionDialog";
import { PaginatedResponse } from "../../../shared/types/pagination";
import {
  ActivityFeedEntry,
  ACTIVITY_API_PAGE_SIZE,
  ACTIVITY_LOG_PAGE_SIZE,
  ACTIVITY_LOG_POLL_INTERVAL_MS,
  ExecutionActivityEntry,
  hasActivityValueChanged,
  isExecutionActive,
  mapExecutionFeedEntries,
  sortActivityFeedEntries,
} from "../../../shared/utils/activityFeed";
import { formatAuditDate, formatAuditTime24Hour } from "../../../shared/utils/formatAuditTime24Hour";
import { formatActivityDuration } from "../../../shared/utils/formatActivityDuration";
import { getAccessToken } from "../../../shared/auth/getAccessToken";

// ── Type Definitions ──────────────────────────────────────────────────────────

interface AutoscaleProfile {
  name: string;
  minInstances: number;
  maxInstances: number;
  defaultInstances: number;
  days: string[];
  startTime: string;
  endTime: string;
  timezone: string;
}

interface VMSSConfig {
  rowKey: string;
  environmentName: string;
  subscriptionId: string;
  resourceGroup: string;
  vmssName: string;
  desiredInstanceCount?: number | null;
  readyInstanceCount?: number | null;
  currentSku?: string | null;
  currentStatus?: string;
  profiles: AutoscaleProfile[];
}

interface SkuOption {
  name: string;
  vCpus?: number;
  memoryGb?: number;
}

interface ProfileFieldChange {
  field?: string;
  before?: string | number | string[];
  after?: string | number | string[];
}

interface ProfileChangeItem {
  profileName?: string;
  changeType?: string;
  fields?: ProfileFieldChange[];
}

interface ParsedChanges {
  previousSku?: string;
  requestedSku?: string;
  sku?: {
    before?: string;
    after?: string;
    changed?: boolean;
  };
  profiles?: ProfileChangeItem[];
}

type DirtyProfileFieldFlags = Partial<Record<keyof AutoscaleProfile, boolean>>;

const isAutoDefaultProfileName = (profileName: string) => {
  const trimmed = (profileName || "").trim();
  if (trimmed.toLowerCase() === "defaultautoscaleprofile") return true;
  if (!trimmed.startsWith("{")) return false;

  try {
    const parsed = JSON.parse(trimmed) as { name?: string };
    return (
      (parsed.name || "").toLowerCase() ===
      "auto created default scale condition"
    );
  } catch {
    return false;
  }
};

const formatAutoscaleProfileSummary = (profile?: AutoscaleProfile) => {
  if (!profile) return "-";
  const schedule = profile.days.length > 0
    ? `; ${profile.days.join(", ")} ${profile.startTime || "-"}-${profile.endTime || "-"} ${profile.timezone || ""}`
    : "";
  return `Min ${profile.minInstances}, Max ${profile.maxInstances}, Default ${profile.defaultInstances}${schedule}`;
};

// ── Utility Functions: Azure URL builders ─────────────────────────────────────

function buildVmssResourceUrl(env: any): string | null {
  const sub = env?.subscriptionId?.trim();
  const rg = env?.resourceGroup?.trim();
  const vmss = env?.vmssName?.trim();
  if (!sub || !rg || !vmss) return null;
  return `https://portal.azure.com/#resource/subscriptions/${encodeURIComponent(sub)}/resourceGroups/${encodeURIComponent(rg)}/providers/Microsoft.Compute/virtualMachineScaleSets/${encodeURIComponent(vmss)}/overview`;
}

// value = Windows timezone ID sent to Azure API
// label = display text matching Azure Portal "(UTC+xx:xx) City" format
const TIMEZONE_OPTIONS: { value: string; label: string }[] = [
  { value: "Dateline Standard Time",          label: "(UTC-12:00) International Date Line West" },
  { value: "UTC-11",                           label: "(UTC-11:00) Coordinated Universal Time-11" },
  { value: "Hawaiian Standard Time",           label: "(UTC-10:00) Hawaii" },
  { value: "Alaskan Standard Time",            label: "(UTC-09:00) Alaska" },
  { value: "Pacific Standard Time (Mexico)",   label: "(UTC-08:00) Baja California" },
  { value: "Pacific Standard Time",            label: "(UTC-08:00) Pacific Time (US & Canada)" },
  { value: "US Mountain Standard Time",        label: "(UTC-07:00) Arizona" },
  { value: "Mountain Standard Time (Mexico)",  label: "(UTC-07:00) Chihuahua, La Paz, Mazatlan" },
  { value: "Mountain Standard Time",           label: "(UTC-07:00) Mountain Time (US & Canada)" },
  { value: "Central America Standard Time",    label: "(UTC-06:00) Central America" },
  { value: "Central Standard Time",            label: "(UTC-06:00) Central Time (US & Canada)" },
  { value: "Central Standard Time (Mexico)",   label: "(UTC-06:00) Guadalajara, Mexico City, Monterrey" },
  { value: "Canada Central Standard Time",     label: "(UTC-06:00) Saskatchewan" },
  { value: "SA Pacific Standard Time",         label: "(UTC-05:00) Bogota, Lima, Quito, Rio Branco" },
  { value: "Eastern Standard Time",            label: "(UTC-05:00) Eastern Time (US & Canada)" },
  { value: "US Eastern Standard Time",         label: "(UTC-05:00) Indiana (East)" },
  { value: "Venezuela Standard Time",          label: "(UTC-04:30) Caracas" },
  { value: "Paraguay Standard Time",           label: "(UTC-04:00) Asuncion" },
  { value: "Atlantic Standard Time",           label: "(UTC-04:00) Atlantic Time (Canada)" },
  { value: "Central Brazilian Standard Time",  label: "(UTC-04:00) Cuiaba" },
  { value: "SA Western Standard Time",         label: "(UTC-04:00) Georgetown, La Paz, Manaus, San Juan" },
  { value: "Pacific SA Standard Time",         label: "(UTC-04:00) Santiago" },
  { value: "Newfoundland Standard Time",       label: "(UTC-03:30) Newfoundland" },
  { value: "E. South America Standard Time",   label: "(UTC-03:00) Brasilia" },
  { value: "Argentina Standard Time",          label: "(UTC-03:00) Buenos Aires" },
  { value: "SA Eastern Standard Time",         label: "(UTC-03:00) Cayenne, Fortaleza" },
  { value: "Greenland Standard Time",          label: "(UTC-03:00) Greenland" },
  { value: "Montevideo Standard Time",         label: "(UTC-03:00) Montevideo" },
  { value: "Bahia Standard Time",              label: "(UTC-03:00) Salvador" },
  { value: "UTC-02",                           label: "(UTC-02:00) Coordinated Universal Time-02" },
  { value: "Azores Standard Time",             label: "(UTC-01:00) Azores" },
  { value: "Cape Verde Standard Time",         label: "(UTC-01:00) Cape Verde Is." },
  { value: "Morocco Standard Time",            label: "(UTC) Casablanca" },
  { value: "UTC",                              label: "(UTC) Coordinated Universal Time" },
  { value: "GMT Standard Time",                label: "(UTC) Dublin, Edinburgh, Lisbon, London" },
  { value: "Greenwich Standard Time",          label: "(UTC) Monrovia, Reykjavik" },
  { value: "W. Europe Standard Time",          label: "(UTC+01:00) Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna" },
  { value: "Central Europe Standard Time",     label: "(UTC+01:00) Belgrade, Bratislava, Budapest, Ljubljana, Prague" },
  { value: "Romance Standard Time",            label: "(UTC+01:00) Brussels, Copenhagen, Madrid, Paris" },
  { value: "Central European Standard Time",   label: "(UTC+01:00) Sarajevo, Skopje, Warsaw, Zagreb" },
  { value: "W. Central Africa Standard Time",  label: "(UTC+01:00) West Central Africa" },
  { value: "Namibia Standard Time",            label: "(UTC+01:00) Windhoek" },
  { value: "Jordan Standard Time",             label: "(UTC+02:00) Amman" },
  { value: "GTB Standard Time",                label: "(UTC+02:00) Athens, Bucharest" },
  { value: "Middle East Standard Time",        label: "(UTC+02:00) Beirut" },
  { value: "Egypt Standard Time",              label: "(UTC+02:00) Cairo" },
  { value: "Syria Standard Time",              label: "(UTC+02:00) Damascus" },
  { value: "E. Europe Standard Time",          label: "(UTC+02:00) E. Europe" },
  { value: "South Africa Standard Time",       label: "(UTC+02:00) Harare, Pretoria" },
  { value: "FLE Standard Time",                label: "(UTC+02:00) Helsinki, Kyiv, Riga, Sofia, Tallinn, Vilnius" },
  { value: "Turkey Standard Time",             label: "(UTC+02:00) Istanbul" },
  { value: "Israel Standard Time",             label: "(UTC+02:00) Jerusalem" },
  { value: "Kaliningrad Standard Time",        label: "(UTC+02:00) Kaliningrad" },
  { value: "Libya Standard Time",              label: "(UTC+02:00) Tripoli" },
  { value: "Arabic Standard Time",             label: "(UTC+03:00) Baghdad" },
  { value: "Arab Standard Time",               label: "(UTC+03:00) Kuwait, Riyadh" },
  { value: "Belarus Standard Time",            label: "(UTC+03:00) Minsk" },
  { value: "Russian Standard Time",            label: "(UTC+03:00) Moscow, St. Petersburg, Volgograd" },
  { value: "E. Africa Standard Time",          label: "(UTC+03:00) Nairobi" },
  { value: "Iran Standard Time",               label: "(UTC+03:30) Tehran" },
  { value: "Arabian Standard Time",            label: "(UTC+04:00) Abu Dhabi, Muscat" },
  { value: "Azerbaijan Standard Time",         label: "(UTC+04:00) Baku" },
  { value: "Russia Time Zone 3",               label: "(UTC+04:00) Izhevsk, Samara" },
  { value: "Mauritius Standard Time",          label: "(UTC+04:00) Port Louis" },
  { value: "Georgian Standard Time",           label: "(UTC+04:00) Tbilisi" },
  { value: "Caucasus Standard Time",           label: "(UTC+04:00) Yerevan" },
  { value: "Afghanistan Standard Time",        label: "(UTC+04:30) Kabul" },
  { value: "West Asia Standard Time",          label: "(UTC+05:00) Ashgabat, Tashkent" },
  { value: "Ekaterinburg Standard Time",       label: "(UTC+05:00) Ekaterinburg" },
  { value: "Pakistan Standard Time",           label: "(UTC+05:00) Islamabad, Karachi" },
  { value: "India Standard Time",              label: "(UTC+05:30) Chennai, Kolkata, Mumbai, New Delhi" },
  { value: "Sri Lanka Standard Time",          label: "(UTC+05:30) Sri Jayawardenepura" },
  { value: "Nepal Standard Time",              label: "(UTC+05:45) Kathmandu" },
  { value: "Central Asia Standard Time",       label: "(UTC+06:00) Astana" },
  { value: "Bangladesh Standard Time",         label: "(UTC+06:00) Dhaka" },
  { value: "N. Central Asia Standard Time",    label: "(UTC+06:00) Novosibirsk" },
  { value: "Myanmar Standard Time",            label: "(UTC+06:30) Yangon (Rangoon)" },
  { value: "SE Asia Standard Time",            label: "(UTC+07:00) Bangkok, Hanoi, Jakarta" },
  { value: "North Asia Standard Time",         label: "(UTC+07:00) Krasnoyarsk" },
  { value: "China Standard Time",              label: "(UTC+08:00) Beijing, Chongqing, Hong Kong, Urumqi" },
  { value: "North Asia East Standard Time",    label: "(UTC+08:00) Irkutsk" },
  { value: "Singapore Standard Time",          label: "(UTC+08:00) Kuala Lumpur, Singapore" },
  { value: "W. Australia Standard Time",       label: "(UTC+08:00) Perth" },
  { value: "Taipei Standard Time",             label: "(UTC+08:00) Taipei" },
  { value: "Ulaanbaatar Standard Time",        label: "(UTC+08:00) Ulaanbaatar" },
  { value: "Tokyo Standard Time",              label: "(UTC+09:00) Osaka, Sapporo, Tokyo" },
  { value: "Korea Standard Time",              label: "(UTC+09:00) Seoul" },
  { value: "Yakutsk Standard Time",            label: "(UTC+09:00) Yakutsk" },
  { value: "Cen. Australia Standard Time",     label: "(UTC+09:30) Adelaide" },
  { value: "AUS Central Standard Time",        label: "(UTC+09:30) Darwin" },
  { value: "E. Australia Standard Time",       label: "(UTC+10:00) Brisbane" },
  { value: "AUS Eastern Standard Time",        label: "(UTC+10:00) Canberra, Melbourne, Sydney" },
  { value: "West Pacific Standard Time",       label: "(UTC+10:00) Guam, Port Moresby" },
  { value: "Tasmania Standard Time",           label: "(UTC+10:00) Hobart" },
  { value: "Magadan Standard Time",            label: "(UTC+10:00) Magadan" },
  { value: "Vladivostok Standard Time",        label: "(UTC+10:00) Vladivostok" },
  { value: "Russia Time Zone 10",              label: "(UTC+11:00) Chokurdakh" },
  { value: "Central Pacific Standard Time",    label: "(UTC+11:00) Solomon Is., New Caledonia" },
  { value: "Russia Time Zone 11",              label: "(UTC+11:00) Magadan" },
  { value: "New Zealand Standard Time",        label: "(UTC+12:00) Auckland, Wellington" },
  { value: "UTC+12",                           label: "(UTC+12:00) Coordinated Universal Time+12" },
  { value: "Fiji Standard Time",               label: "(UTC+12:00) Fiji" },
  { value: "Kamchatka Standard Time",          label: "(UTC+12:00) Petropavlovsk-Kamchatsky - Old" },
  { value: "Tonga Standard Time",              label: "(UTC+13:00) Nuku'alofa" },
  { value: "Samoa Standard Time",              label: "(UTC+13:00) Samoa" },
  { value: "Line Islands Standard Time",       label: "(UTC+14:00) Kiritimati Island" },
];

const ENVIRONMENT_OPTIONS = [
  { value: "AccelerusDev",  label: "Accelerus Dev" },
  { value: "AccelerusProd", label: "Accelerus Prod" },
];

const DAY_OPTIONS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

interface WorkerScalingProps {
  env: "AccelerusDev" | "AccelerusProd";
}

type PendingApply = "scaling" | "size" | null;

// ── Main Component: WorkerScaling ────────────────────────────────────────────

const WorkerScaling = ({ env }: WorkerScalingProps) => {
  // ── State Management & Configuration
  const { instance, accounts } = useMsal();
  const { setLoading } = useLoading();
  const apiUrl = `${process.env.REACT_APP_API_URL}/api/accelerus/worker-scaling`;

  // ── Constants: VM Size Tiers
  // Fixed vCPU/RAM tiers — only sizes without local storage (N/A), matching user-approved options
  const VCPU_TIERS: { vcpu: number; ram: number }[] = [
    { vcpu: 2,  ram: 8   },
    { vcpu: 4,  ram: 16  },
    { vcpu: 8,  ram: 32  },
    { vcpu: 16, ram: 64  },
    { vcpu: 32, ram: 64  },
  ];
  const VCPU_VALUES = VCPU_TIERS.map(t => t.vcpu);
  const ALLOWED_SKU_SPECS = new Set(VCPU_TIERS.map(t => `${t.vcpu}:${t.ram}`));
  const normalizeVcpu = (v: number) => {
    const i = VCPU_VALUES.indexOf(v);
    return i >= 0 ? (i / (VCPU_VALUES.length - 1)) * 100 : 0;
  };
  const denormalizeVcpu = (val: number): number => {
    const step = 100 / (VCPU_VALUES.length - 1);
    return VCPU_VALUES[Math.round(val / step)] ?? VCPU_VALUES[0];
  };

  const [environments, setEnvironments] = useState<VMSSConfig[]>([]);
  const [editableProfiles, setEditableProfiles] = useState<AutoscaleProfile[]>([]);
  const [dirtyProfileFields, setDirtyProfileFields] = useState<Record<string, DirtyProfileFieldFlags>>({});
  const [sku, setSku] = useState<string>("");
  const [selectedVcpu, setSelectedVcpu] = useState<number>(4);
  // Fast slider drags can resolve out of order, keep only the latest tier
  const selectedVcpuRef = useRef(selectedVcpu);
  selectedVcpuRef.current = selectedVcpu;
  const [resolvedSku, setResolvedSku] = useState<{ skuName: string; vCpus: number; memoryGb: number } | null>(null);
  const [availableSkus, setAvailableSkus] = useState<SkuOption[]>([]);
  const [skusLoading, setSkusLoading] = useState<boolean>(false);
  const [unavailableTiers, setUnavailableTiers] = useState<Set<number>>(new Set());
  const [activityFeedEntries, setActivityFeedEntries] = useState<ActivityFeedEntry[]>([]);
  const [activityLogsLoading, setActivityLogsLoading] = useState<boolean>(false);
  const [activityLogsPage, setActivityLogsPage] = useState<number>(1);
  const [activityLogsTotalPages, setActivityLogsTotalPages] = useState<number>(1);
  const [activityLogsTotalCount, setActivityLogsTotalCount] = useState<number>(0);
  const [hasActiveExecutions, setHasActiveExecutions] = useState<boolean>(false);
  // Last poll result, so we know when a run just finished
  const hadActiveExecutionsRef = useRef(false);
  const [activeTab, setActiveTab] = useState<FeatureTab>("operations");
  const [pendingApply, setPendingApply] = useState<PendingApply>(null);
  // Dev and Prod routes share this instance, fetches check this to drop late responses for the old env
  const selectedEnvRef = useRef(env);
  selectedEnvRef.current = env;

  const formatChangeValue = (value: string | number | string[] | undefined) => {
    if (Array.isArray(value)) return value.join(", ");
    if (value === undefined || value === null || value === "") return "-";
    return String(value);
  };

  const formatSkuWithSpecs = (skuName?: string) => {
    if (!skuName) return "-";

    const matchedSku = availableSkus.find((sku) => sku.name === skuName);
    if (matchedSku?.vCpus && matchedSku.memoryGb != null) {
      return `${skuName} (${matchedSku.vCpus} vCPU / ${matchedSku.memoryGb} GB RAM)`;
    }

    const tierMatch = /^Standard_D(\d+)[A-Za-z0-9_]*$/i.exec(skuName);
    const tierVcpu = tierMatch ? Number.parseInt(tierMatch[1], 10) : NaN;
    const tier = VCPU_TIERS.find((item) => item.vcpu === tierVcpu);
    if (tier) {
      return `${skuName} (${tier.vcpu} vCPU / ${tier.ram} GB RAM)`;
    }

    return skuName;
  };

  const dayOrder = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const formatFieldLabel = (value: string) =>
    value
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/\b\w/g, (char) => char.toUpperCase());

  const sortDays = (value: unknown) => {
    if (!Array.isArray(value)) return formatChangeValue(value as string | number | string[] | undefined);

    return value
      .map((day) => String(day))
      .sort((a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b))
      .join(", ");
  };

  const extractChangeLines = (changesJson?: string) => {
    if (!changesJson) return [] as string[];
    try {
      const parsed = JSON.parse(changesJson) as ParsedChanges;
      const lines: string[] = [];

      const previousSku = parsed.sku?.before ?? parsed.previousSku;
      const requestedSku = parsed.sku?.after ?? parsed.requestedSku;
      const isSkuChanged =
        parsed.sku?.changed
        ?? (String(previousSku ?? "").trim().toLowerCase() !== String(requestedSku ?? "").trim().toLowerCase());

      if (isSkuChanged && (previousSku || requestedSku)) {
        lines.push(`Worker Role (VMSS) Size: ${formatSkuWithSpecs(previousSku)} -> ${formatSkuWithSpecs(requestedSku)}`);
      }

      const profiles = Array.isArray(parsed?.profiles) ? parsed.profiles : [];
      profiles.forEach((profile) => {
        const profileName = profile.profileName || "Profile";
        if (profile.changeType === "new_profile") {
          lines.push(`${profileName}: New profile added.`);
          return;
        }

        const fields = Array.isArray(profile.fields) ? profile.fields : [];
        fields.forEach((fieldChange) => {
          const rawFieldName = fieldChange.field || "field";
          const fieldName = formatFieldLabel(rawFieldName);
          const beforeValue = rawFieldName === "days" ? sortDays(fieldChange.before) : formatChangeValue(fieldChange.before);
          const afterValue = rawFieldName === "days" ? sortDays(fieldChange.after) : formatChangeValue(fieldChange.after);
          // Compare sorted days so a reorder alone isn't a change
          const isChanged = rawFieldName === "days"
            ? hasActivityValueChanged(beforeValue, afterValue)
            : hasActivityValueChanged(fieldChange.before, fieldChange.after);
          if (!isChanged) return;
          lines.push(
            `${profileName} • ${fieldName}: ${beforeValue} -> ${afterValue}`
          );
        });
      });

      return lines.slice(0, 12);
    } catch {
      return [] as string[];
    }
  };

  const getActivityChangeLines = (log: ActivityFeedEntry) => {
    return extractChangeLines(log.detailsJson);
  };

  const buildActivityFeed = (executionItems: ExecutionActivityEntry[]): ActivityFeedEntry[] => {
    return sortActivityFeedEntries(mapExecutionFeedEntries(executionItems));
  };


  const fetchSkus = useCallback(async (envKey: string) => {
    try {
      setSkusLoading(true);
      const token = await getAccessToken();
      const response = await axios.get(`${apiUrl}/${envKey}/skus`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (envKey !== selectedEnvRef.current) return;
      setAvailableSkus(response.data as SkuOption[]);
    } catch {
      if (envKey !== selectedEnvRef.current) return;
      toast.error("Failed to load available SKUs.");
      setAvailableSkus([]);
    } finally {
      setSkusLoading(false);
    }
  }, [apiUrl]);

  // Resolve SKU name from vCPU + RAM count via backend (checks Azure availability)
  const resolveSkuByVcpu = async (envKey: string, vcpu: number) => {
    const tier = VCPU_TIERS.find(t => t.vcpu === vcpu);
    try {
      const token = await getAccessToken();
      const res = await axios.post(
        `${apiUrl}/${envKey}/sku-by-vcpu`,
        { vCpus: vcpu, memoryGb: tier?.ram ?? null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (envKey !== selectedEnvRef.current || vcpu !== selectedVcpuRef.current) return;
      setResolvedSku(res.data);
      setSku(res.data.skuName);
      // Clear unavailable flag for this tier if it now resolves successfully
      setUnavailableTiers(prev => { const next = new Set(prev); next.delete(vcpu); return next; });
    } catch {
      if (envKey !== selectedEnvRef.current || vcpu !== selectedVcpuRef.current) return;
      setResolvedSku(null);
      setSku("");
      setUnavailableTiers(prev => new Set(prev).add(vcpu));
      const ramDesc = tier ? ` / ${tier.ram} GB RAM` : "";
      toast.error(`${vcpu} vCPU${ramDesc} is not available in this environment's region.`);
    }
  };

  // ── API Functions: Data Fetching
  const fetchData = useCallback(async (showErrorToast = true, showGlobalLoading = true) => {
    try {
      const token = await getAccessToken();
      if (showGlobalLoading) {
        setLoading(true);
      }
      const response = await axios.get(apiUrl, { headers: { Authorization: `Bearer ${token}` } });
      if (env !== selectedEnvRef.current) return;
      const configs = response.data as VMSSConfig[];
      setEnvironments(configs);
      const current = configs.find((c) => c.rowKey === env);
      if (current) {
        setEditableProfiles(
          current.profiles
            .filter((p) => !isAutoDefaultProfileName(p.name))
            .map((p) => ({ ...p }))
        );
        setDirtyProfileFields({});
        setSku(current.currentSku || "");
        // Init slider to closest tier based on available skus (fetched separately)
        // Default to 4 vCPU until skus load
        setResolvedSku(null);
      }
    } catch {
      if (showErrorToast && env === selectedEnvRef.current) {
        toast.error("Failed to load worker settings.");
      }
    } finally {
      if (showGlobalLoading) {
        setLoading(false);
      }
    }
  }, [apiUrl, env, setLoading]);

  const fetchActivityLogs = useCallback(async (envKey: string, page = 1, showErrorToast = true, showLoadingState = true) => {
    try {
      if (showLoadingState) {
        setActivityLogsLoading(true);
      }
      const token = await getAccessToken();
      const executionItems = await axios.get<PaginatedResponse<ExecutionActivityEntry>>(`${apiUrl}/${envKey}/executions`, {
        params: { pageSize: ACTIVITY_API_PAGE_SIZE },
        headers: { Authorization: `Bearer ${token}` },
      }).then((response) => response.data.items);
      if (envKey !== selectedEnvRef.current) return;
      const executionFeed = buildActivityFeed(executionItems);
      const totalCount = executionFeed.length;
      const totalPages = Math.max(1, Math.ceil(totalCount / ACTIVITY_LOG_PAGE_SIZE));
      const safePage = Math.min(Math.max(page, 1), totalPages);
      const pagedItems = executionFeed.slice(
        (safePage - 1) * ACTIVITY_LOG_PAGE_SIZE,
        safePage * ACTIVITY_LOG_PAGE_SIZE,
      );

      setActivityFeedEntries(pagedItems);
      setActivityLogsPage(safePage);
      setActivityLogsTotalPages(totalPages);
      setActivityLogsTotalCount(totalCount);
      const hasActive = executionItems.some((item) => isExecutionActive(item.status));
      setHasActiveExecutions(hasActive);
      // Run just finished, reload size and worker count
      if (!hasActive && hadActiveExecutionsRef.current) {
        void fetchData(false, false);
      }
      hadActiveExecutionsRef.current = hasActive;
    } catch {
      // Failed poll keeps the last rows and keeps polling
      if (!showLoadingState || envKey !== selectedEnvRef.current) {
        return;
      }
      if (showErrorToast) {
        toast.error("Failed to load activity logs.");
      }
      setActivityFeedEntries([]);
      setActivityLogsPage(1);
      setActivityLogsTotalPages(1);
      setActivityLogsTotalCount(0);
      setHasActiveExecutions(false);
      hadActiveExecutionsRef.current = false;
    } finally {
      if (showLoadingState) {
        setActivityLogsLoading(false);
      }
    }
  }, [apiUrl, fetchData]);

  const handleProfileChange = (
    index: number,
    field: keyof AutoscaleProfile,
    value: number | string | string[]
  ) => {
    const profileName = editableProfiles[index]?.name;
    if (profileName) {
      setDirtyProfileFields((prev) => ({
        ...prev,
        [profileName]: {
          ...(prev[profileName] ?? {}),
          [field]: true,
        },
      }));
    }
    setEditableProfiles((prev) =>
      prev.map((p, i) => (i === index ? { ...p, [field]: value } : p))
    );
  };

  const handleDayToggle = (profileIndex: number, day: string) => {
    const profileName = editableProfiles[profileIndex]?.name;
    if (profileName) {
      setDirtyProfileFields((prev) => ({
        ...prev,
        [profileName]: {
          ...(prev[profileName] ?? {}),
          days: true,
        },
      }));
    }
    setEditableProfiles((prev) =>
      prev.map((p, i) => {
        if (i !== profileIndex) return p;
        const days = p.days.includes(day) ? p.days.filter((d) => d !== day) : [...p.days, day];
        return { ...p, days };
      })
    );
  };

  // Apply scaling profiles only — SKU is kept as-is from current Azure state
  const handleApplyScaling = async () => {
    try {
      setLoading(true);
      const token = await getAccessToken();
      const latestResponse = await axios.get(apiUrl, { headers: { Authorization: `Bearer ${token}` } });
      const latestConfigs = latestResponse.data as VMSSConfig[];
      const latestCurrentEnv = latestConfigs.find((config) => config.rowKey === env);
      const latestProfiles = (latestCurrentEnv?.profiles ?? []).filter((profile) => !isAutoDefaultProfileName(profile.name));
      const latestByName = new Map(latestProfiles.map((profile) => [profile.name.toLowerCase(), profile]));

      const payloadProfiles = editableProfiles
        .filter((profile) => !isAutoDefaultProfileName(profile.name))
        .map((profile) => {
          const latestProfile = latestByName.get(profile.name.toLowerCase()) ?? profile;
          const dirty = dirtyProfileFields[profile.name] ?? {};

          return {
            profileName: profile.name,
            minInstances: dirty.minInstances ? profile.minInstances : latestProfile.minInstances,
            maxInstances: dirty.maxInstances ? profile.maxInstances : latestProfile.maxInstances,
            defaultInstances: dirty.defaultInstances ? profile.defaultInstances : latestProfile.defaultInstances,
            days: dirty.days ? profile.days : latestProfile.days,
            startTime: dirty.startTime ? profile.startTime : latestProfile.startTime,
            endTime: dirty.endTime ? profile.endTime : latestProfile.endTime,
            timezone: dirty.timezone ? profile.timezone : latestProfile.timezone,
          };
        });

      // Validate same conditions as Azure Portal
      for (const p of payloadProfiles) {
        if (p.maxInstances < p.minInstances) {
          toast.error(`"${p.profileName}": Maximum workers cannot be lower than minimum workers.`);
          return;
        }
        if (p.defaultInstances < p.minInstances || p.defaultInstances > p.maxInstances) {
          toast.error(`"${p.profileName}": Default workers must be between the minimum and maximum.`);
          return;
        }
      }

      const activeSku = latestCurrentEnv?.currentSku || environments.find((c) => c.rowKey === env)?.currentSku || sku;
      await axios.post(
        `${apiUrl}/${env}/update`,
        {
          profiles: payloadProfiles,
          sku: activeSku,
          actionType: "scaling_profiles",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Automatic Scaling update is queued.");
      setDirtyProfileFields({});
      setHasActiveExecutions(true);
      void fetchData(false, false);
      void fetchActivityLogs(env, 1, false, false);
    } catch (error) {
      toast.error(`Failed to apply scaling: ${getApiErrorMessage(error, "The scaling request failed.")}`);
    } finally {
      setLoading(false);
    }
  };

  // Apply SKU only
  const handleApplySku = async () => {
    try {
      setLoading(true);
      const token = await getAccessToken();
      const currentProfiles =
        environments
          .find((config) => config.rowKey === env)
          ?.profiles.filter((profile) => !isAutoDefaultProfileName(profile.name)) ??
        editableProfiles;

      // Send the persisted profiles from Azure/table state so this action only changes SKU.
      const response = await axios.post(
        `${apiUrl}/${env}/update`,
        {
          profiles: currentProfiles.map((p) => ({
            profileName: p.name,
            minInstances: p.minInstances,
            maxInstances: p.maxInstances,
            defaultInstances: p.defaultInstances,
            days: p.days,
            startTime: p.startTime,
            endTime: p.endTime,
            timezone: p.timezone,
          })),
          sku,
          actionType: "realtime_compute_size",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data?.message ?? "Worker Role (VMSS) size update is queued.");
      setHasActiveExecutions(true);
      void fetchActivityLogs(env, 1, false, false);
    } catch (error) {
      toast.error(`Failed to apply Worker Role (VMSS) size: ${getApiErrorMessage(error, "The SKU request failed.")}`);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmApply = () => {
    const action = pendingApply;
    setPendingApply(null);

    if (action === "scaling") {
      void handleApplyScaling();
    } else if (action === "size") {
      void handleApplySku();
    }
  };

  // ── Effects: Data Fetching & Polling
  // Init slider once both environments + availableSkus are loaded
  useEffect(() => {
    if (availableSkus.length === 0 || environments.length === 0) return;
    const envConfig = environments.find(c => c.rowKey === env);
    if (!envConfig?.currentSku) return;
    const current = availableSkus.find(s => s.name === envConfig.currentSku);
    if (current?.vCpus && current?.memoryGb != null && ALLOWED_SKU_SPECS.has(`${current.vCpus}:${current.memoryGb}`)) {
      setSelectedVcpu(current.vCpus);
      setResolvedSku({ skuName: current.name, vCpus: current.vCpus, memoryGb: current.memoryGb ?? 0 });
      setSku(current.name);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableSkus, environments]);

  useEffect(() => {
    if (instance && accounts.length > 0) {
      void fetchData();
      void fetchSkus(env);
      void fetchActivityLogs(env, 1);
    }
  }, [accounts, fetchActivityLogs, fetchData, fetchSkus, instance, env]);

  useEffect(() => {
    if (!hasActiveExecutions) {
      return;
    }

    const intervalId = window.setInterval(() => {
      void fetchActivityLogs(env, activityLogsPage, false, false);
    }, ACTIVITY_LOG_POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [activityLogsPage, fetchActivityLogs, hasActiveExecutions, env]);

  const currentEnv = environments.find((c) => c.rowKey === env);
  const envLabel = ENVIRONMENT_OPTIONS.find((o) => o.value === env)?.label || env;
  const activityLogRows: ActivityLogTableItem[] = activityFeedEntries.map((log) => ({
    id: log.id,
    dateText: formatAuditDate(log.startedAtUtc),
    timeText: formatAuditTime24Hour(log.startedAtUtc),
    durationText: formatActivityDuration(log.durationMs),
    runningAtUtc: log.runningAtUtc,
    actionText: log.actionType === "realtime_compute_size"
      ? "Set Worker Role (VMSS) Size"
      : "Update Automatic Scaling",
    byName: log.actorName || "Unknown",
    byEmail: log.actorEmail,
    status: log.status,
    changeLines: getActivityChangeLines(log),
    errorMessage: log.errorMessage,
    pipelineRunUrl: log.pipelineRunUrl,
  }));
  // Only show SKUs from the approved tiers (no local storage)
  const filteredSkus = availableSkus.filter(s =>
    s.vCpus != null && s.memoryGb != null &&
    ALLOWED_SKU_SPECS.has(`${s.vCpus}:${s.memoryGb}`)
  );
  const currentSkuInfo = currentEnv?.currentSku
    ? filteredSkus.find((sku) => sku.name === currentEnv.currentSku)
      ?? availableSkus.find((sku) => sku.name === currentEnv.currentSku)
    : undefined;
  const currentSkuDetails = currentSkuInfo?.vCpus != null && currentSkuInfo.memoryGb != null
    ? ` (${currentSkuInfo.vCpus} vCPU / ${currentSkuInfo.memoryGb} GB RAM)`
    : "";
  const currentSizeLabel = currentEnv
    ? currentEnv.currentSku
      ? `${currentEnv.currentSku}${currentSkuDetails}`
      : "Not available"
    : undefined;
  // ── Render: UI Layout
  return (
    <div className="feature-workspace">
      <FeatureTabs activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === "information" && (
        <FeatureInformationPanel summary="Sets worker capacity for background jobs.">
          <h3 className="text-sm font-semibold text-slate-800">How workers are scaled</h3>
          <ul className="mt-3 list-disc space-y-1 pl-4 text-xs leading-5 text-slate-600">
            <li>Automatic Scaling sets the worker limits and schedules.</li>
            <li>Azure uses the number of background jobs waiting in the queue to add or remove workers within those limits.</li>
            <li>Worker Role (VMSS) Size sets the CPU and RAM for each worker.</li>
            <li>Execution: Azure Resource Manager SDK.</li>
          </ul>
          <h3 className="mt-4 text-sm font-semibold text-slate-800">Time and impact</h3>
          <ul className="mt-3 list-disc space-y-1 pl-4 text-xs leading-5 text-slate-600">
            <li>Changing Worker Role (VMSS) Size restarts the VMSS workers. Workers may be unavailable and background jobs may be delayed. This takes about 1 to 10 minutes.</li>
          </ul>
          <h3 className="mt-4 text-sm font-semibold text-slate-800">Before you change</h3>
          <ul className="mt-3 list-disc space-y-1 pl-4 text-xs leading-5 text-slate-600">
            <li>More workers let more background jobs run in parallel. Fewer workers may delay queued jobs.</li>
            <li>Use Automatic Scaling for more parallel jobs. Use Worker Role (VMSS) Size when each worker needs more CPU or RAM.</li>
            <li>Apply Worker Role (VMSS) Size outside busy report periods when possible.</li>
          </ul>
        </FeatureInformationPanel>
      )}

      {activeTab === "operations" && (
        <div className="mt-2" id="feature-panel-operations" role="tabpanel" aria-labelledby="feature-tab-operations" tabIndex={0}>
      <ResourceContextPanel
        resourceNameLabel="Worker Role (VMSS)"
        resourceName={currentEnv?.vmssName}
        status={currentEnv?.currentStatus}
        currentSize={currentSizeLabel}
        currentSizeValueLabel=""
        currentSizeDetails={currentEnv
          ? [{
              label: "Workers",
              // Ready of requested, so a resize in progress does not look finished
              value: currentEnv.desiredInstanceCount == null
                ? "Unavailable"
                : currentEnv.readyInstanceCount == null
                  ? `${currentEnv.desiredInstanceCount} requested`
                  : currentEnv.readyInstanceCount === currentEnv.desiredInstanceCount
                    ? `${currentEnv.readyInstanceCount} ready`
                    : `${currentEnv.readyInstanceCount} of ${currentEnv.desiredInstanceCount} ready`,
            }]
          : undefined}
        portalUrl={currentEnv ? buildVmssResourceUrl(currentEnv) : null}
      />
      <section className="workspace-card mt-2 w-full overflow-hidden border border-slate-200 bg-white">
      <div className="px-5 py-4">
        <h3 className="text-sm font-bold leading-5 text-slate-800">Automatic Scaling</h3>
          <p className="mt-1 text-[11px] leading-4 text-slate-500">
          Set worker limits and schedules.
        </p>
        <hr className="my-3 border-slate-200" />

        {editableProfiles.filter((p) => !isAutoDefaultProfileName(p.name)).length === 0 ? (
          <p className="text-sm text-gray-500">No autoscale profiles found for this environment.</p>
        ) : (
          <div className="space-y-3">
            {editableProfiles.map((profile, index) => isAutoDefaultProfileName(profile.name) ? null : (
              <div key={profile.name} className="rounded-sm border border-slate-200 overflow-hidden">
                {/* Condition header bar — mimics Azure Portal style */}
                <div className="bg-slate-100 border-b-2 border-slate-300 px-4 py-3 flex items-center gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Condition</span>
                  <span className="text-sm font-bold text-slate-800">{profile.name}</span>
                </div>
                <div className="p-3">

                {/* Instance limits */}
                {(() => {
                  const maxBelowMin = profile.maxInstances < profile.minInstances;
                  const defOutOfRange = profile.defaultInstances < profile.minInstances || profile.defaultInstances > profile.maxInstances;
                  return (
                  <div className="grid grid-cols-1 gap-4 mb-2 md:grid-cols-3">
                    <div>
                      <label htmlFor={`profile-${index}-min-instances`} className="block text-xs font-medium text-gray-600 mb-1">Minimum Workers</label>
                      <span className="block text-xs font-normal text-gray-400 mb-1">Minimum number of workers.</span>
                      <input
                        type="number"
                        id={`profile-${index}-min-instances`}
                        min={1}
                        value={profile.minInstances}
                        onChange={(e) => handleProfileChange(index, "minInstances", parseInt(e.target.value, 10) || 1)}
                        className={`block w-full p-2 border rounded-sm text-sm ${maxBelowMin ? "border-red-400 bg-red-50" : ""}`}
                      />
                    </div>
                    <div>
                      <label htmlFor={`profile-${index}-max-instances`} className="block text-xs font-medium text-gray-600 mb-1">Maximum Workers</label>
                      <span className="block text-xs font-normal text-gray-400 mb-1">Maximum number of workers.</span>
                      <input
                        type="number"
                        id={`profile-${index}-max-instances`}
                        min={1}
                        value={profile.maxInstances}
                        onChange={(e) => handleProfileChange(index, "maxInstances", parseInt(e.target.value, 10) || 1)}
                        className={`block w-full p-2 border rounded-sm text-sm ${maxBelowMin ? "border-red-400 bg-red-50" : ""}`}
                      />
                      {maxBelowMin && (
                        <span className="text-xs text-red-500 mt-1 block">Maximum workers cannot be lower than minimum workers.</span>
                      )}
                    </div>
                    <div>
                      <label htmlFor={`profile-${index}-default-instances`} className="block text-xs font-medium text-gray-600 mb-1">Default Workers</label>
                      <span className="block text-xs font-normal text-gray-400 mb-1">Starting number of workers.</span>
                      <input
                        type="number"
                        id={`profile-${index}-default-instances`}
                        min={1}
                        value={profile.defaultInstances}
                        onChange={(e) => handleProfileChange(index, "defaultInstances", parseInt(e.target.value, 10) || 1)}
                        className={`block w-full p-2 border rounded-sm text-sm ${defOutOfRange ? "border-red-400 bg-red-50" : ""}`}
                      />
                      {defOutOfRange && (
                        <span className="text-xs text-red-500 mt-1 block">Default workers must be between the minimum and maximum.</span>
                      )}
                    </div>
                  </div>
                  );
                })()}

                {/* Schedule */}
                {(profile.days.length > 0 || profile.startTime) && (
                  <div className="border-t border-slate-100 pt-3 mt-1">
                    <p className="text-xs font-medium text-gray-600 mb-2">Schedule</p>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {DAY_OPTIONS.map((day) => (
                        <button
                          key={day}
                          type="button"
                          onClick={() => handleDayToggle(index, day)}
                          className={`px-2 py-1 rounded-sm text-xs font-medium border transition-colors ${
                            profile.days.includes(day)
                              ? "bg-blue-600 text-white border-blue-600"
                              : "bg-white text-gray-600 border-gray-300 hover:border-blue-400"
                          }`}
                        >
                          {day.slice(0, 3)}
                        </button>
                      ))}
                    </div>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      <div>
                        <label htmlFor={`profile-${index}-start-time`} className="block text-xs font-medium text-gray-600 mb-1">Start Time (HH:mm)</label>
                        <span className="block text-xs font-normal text-gray-400 mb-1">Profile becomes active.</span>
                        <input
                          type="time"
                          id={`profile-${index}-start-time`}
                          value={profile.startTime}
                          onChange={(e) => handleProfileChange(index, "startTime", e.target.value)}
                          className="block w-full p-2 border rounded-sm text-sm"
                        />
                      </div>
                      <div>
                        <label htmlFor={`profile-${index}-end-time`} className="block text-xs font-medium text-gray-600 mb-1">End Time (HH:mm)</label>
                        <span className="block text-xs font-normal text-gray-400 mb-1">Leave blank if no end.</span>
                        <input
                          type="time"
                          id={`profile-${index}-end-time`}
                          value={profile.endTime}
                          onChange={(e) => handleProfileChange(index, "endTime", e.target.value)}
                          className="block w-full p-2 border rounded-sm text-sm"
                        />
                      </div>
                      <div>
                        <label htmlFor={`profile-${index}-timezone`} className="block text-xs font-medium text-gray-600 mb-1">Timezone</label>
                        <span className="block text-xs font-normal text-gray-400 mb-1">As defined in Azure.</span>
                        <select
                          id={`profile-${index}-timezone`}
                          value={profile.timezone}
                          onChange={(e) => handleProfileChange(index, "timezone", e.target.value)}
                          className="block w-full p-2 border rounded-sm text-sm"
                        >
                          {TIMEZONE_OPTIONS.map((tz) => (
                            <option key={tz.value} value={tz.value}>{tz.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                )}
                </div>{/* end p-3 */}
              </div>
            ))}
          </div>
        )}

        <div className="mt-3 flex justify-start">
        <button type="button" onClick={() => setPendingApply("scaling")} className={btn.applySmall}>
          Apply Automatic Scaling
        </button>
        </div>
      </div>

      <div className="border-t border-slate-200 px-5 py-4">
        <h3 className="text-sm font-bold leading-5 text-slate-800">Worker Role (VMSS) Size</h3>
        <p className="mt-1 text-[11px] leading-4 text-slate-500">
          Changes the CPU and RAM for each worker. This restarts workers. Workers may be unavailable and background jobs may be delayed. This takes about 1 to 10 minutes.
        </p>
        <hr className="my-3 border-slate-200" />

        <div className="rounded-sm border border-slate-200 bg-white p-3">
          <Box className="px-2">
            <label className="block text-xs font-medium text-gray-700 mb-2">
              Size
              {skusLoading && (
                <span className="ml-2 text-xs text-gray-400">Loading available sizes from Azure...</span>
              )}
            </label>
            <Slider
              sx={{ ...standardSliderSx, ...selectedSliderMarkSx(VCPU_TIERS.findIndex((tier) => tier.vcpu === selectedVcpu)) }}
              value={normalizeVcpu(selectedVcpu)}
              step={null}
              marks={VCPU_TIERS.map((_, i) => ({ value: (i / (VCPU_TIERS.length - 1)) * 100 }))}
              min={0}
              max={100}
              valueLabelDisplay="auto"
              valueLabelFormat={() => {
                const ram = resolvedSku?.memoryGb ?? filteredSkus.find(s => s.vCpus === selectedVcpu)?.memoryGb;
                return resolvedSku
                  ? `${resolvedSku.skuName} (${selectedVcpu} vCPU / ${ram ?? "?"} GB RAM)`
                  : `${selectedVcpu} vCPU / ${ram ?? "?"} GB RAM`;
              }}
              getAriaValueText={() => `${selectedVcpu} vCPU`}
              onChange={(_: Event, val: number | number[]) => {
                const vcpu = denormalizeVcpu(val as number);
                setSelectedVcpu(vcpu);
                resolveSkuByVcpu(env, vcpu);
              }}
            />
            {/* Custom tier labels — all tiers clickable; unavailable tiers are dimmed */}
            <div className="flex justify-between mt-0.5 px-0">
              {VCPU_TIERS.map((t) => {
                const isSelected = t.vcpu === selectedVcpu;
                const isUnavailable = unavailableTiers.has(t.vcpu);
                return (
                  <button
                    key={t.vcpu}
                    type="button"
                    onClick={() => { setSelectedVcpu(t.vcpu); resolveSkuByVcpu(env, t.vcpu); }}
                    className={`flex flex-col items-center rounded-sm py-0.5 transition-colors w-10 ${
                      isUnavailable
                        ? "text-gray-300 cursor-not-allowed line-through"
                        : isSelected
                          ? "text-blue-600 font-semibold"
                          : "text-gray-400 hover:text-blue-500"
                    }`}
                  >
                    <span className="text-[10px] leading-tight whitespace-nowrap">{t.vcpu} vCPU</span>
                    <span className="text-[10px] leading-tight whitespace-nowrap">
                      {t.ram} GB RAM
                    </span>
                  </button>
                );
              })}
            </div>
          </Box>
        </div>

        <div className="mt-3 flex justify-start">
        <button type="button" onClick={() => setPendingApply("size")} disabled={!resolvedSku || skusLoading} className={btn.applySmall}>
          Apply Worker Role (VMSS) Size
        </button>
        </div>
      </div>
      </section>

      <ActivityLogTable
        title="Activity Log"
        description="Tracks worker capacity changes and their progress."
        items={activityLogRows}
        loading={activityLogsLoading}
        page={activityLogsPage}
        totalPages={activityLogsTotalPages}
        totalCount={activityLogsTotalCount}
        onRefresh={() => fetchActivityLogs(env, activityLogsPage)}
        onPageChange={(pageNumber) => fetchActivityLogs(env, pageNumber)}
      />
        </div>
      )}

      <ConfirmActionDialog
        open={pendingApply !== null}
        title={pendingApply === "scaling" ? "Confirm Automatic Scaling" : "Confirm Worker Role (VMSS) Size"}
        description={pendingApply === "scaling" ? "Apply these automatic scaling changes?" : "Apply this Worker Role (VMSS) size change?"}
        confirmLabel={pendingApply === "scaling" ? "Apply Automatic Scaling" : "Apply Worker Role (VMSS) Size"}
        onConfirm={handleConfirmApply}
        onCancel={() => setPendingApply(null)}
      >
        <div className="rounded-sm border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-gray-700">
          <p className="font-medium text-gray-800">Change to apply</p>
          <div className="mt-2 space-y-1 text-xs text-gray-600">
            <p>
              Environment:{" "}
              <span className="font-semibold text-gray-800">
                {currentEnv?.environmentName ?? envLabel}
              </span>
            </p>
            {pendingApply === "size" && resolvedSku && (
              <>
                <p>Current Worker Role (VMSS) Size: <span className="font-semibold text-gray-800">{currentSizeLabel}</span></p>
                <p>Target Worker Role (VMSS) Size: <span className="font-bold text-blue-700">{resolvedSku.skuName} ({resolvedSku.vCpus} vCPU / {resolvedSku.memoryGb} GB RAM)</span></p>
              </>
            )}
            {pendingApply === "scaling" && (
              editableProfiles.filter((profile) => !isAutoDefaultProfileName(profile.name)).map((profile) => {
                const currentProfile = currentEnv?.profiles.find((item) => item.name.toLowerCase() === profile.name.toLowerCase());
                return (
                  <div key={profile.name} className="border-t border-blue-200 pt-1 first:border-t-0 first:pt-0">
                    <p className="font-semibold text-gray-800">{profile.name}</p>
                    <p>Current: {formatAutoscaleProfileSummary(currentProfile)}</p>
                    <p>Target: {formatAutoscaleProfileSummary(profile)}</p>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </ConfirmActionDialog>
    </div>
  );
};

export default WorkerScaling;
